import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { useId } from 'react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/utils';

import { FieldChrome } from './FieldChrome';
import { baseControlClasses } from './Input';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  value: string;
  onValueChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
}

// Radix forbids an empty-string Item value (it's reserved to mean "no
// selection"), but several call sites use "" for an "All languages" /
// "Not specified" style option. Remap "" <-> this sentinel at the boundary
// so callers can keep passing/receiving plain "" like the old native <select>.
const EMPTY_VALUE_SENTINEL = '__brainkey_empty__';

export function Select({
  label,
  hint,
  error,
  optional,
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  id,
  className,
  ...aria
}: SelectProps): JSX.Element {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const invalid = error !== undefined && error !== '';
  const radixValue = value === '' ? EMPTY_VALUE_SENTINEL : value;
  const selected = options.find((o) => o.value === value);

  return (
    <FieldChrome label={label} hint={hint} error={error} optional={optional} id={fieldId}>
      <SelectPrimitive.Root
        value={radixValue}
        onValueChange={(next) => onValueChange(next === EMPTY_VALUE_SENTINEL ? '' : next)}
        disabled={disabled ?? false}
      >
        <SelectPrimitive.Trigger
          id={fieldId}
          aria-invalid={invalid || undefined}
          className={cn(
            baseControlClasses,
            'flex items-center justify-between gap-2 text-left',
            invalid
              ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-200 dark:border-rose-500/50 dark:focus:ring-rose-500/20'
              : 'focus:ring-zinc-900/10 dark:focus:ring-brand-500/20',
            'data-[state=open]:ring-2 data-[state=open]:ring-zinc-900/10 dark:data-[state=open]:ring-brand-500/20',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            className,
          )}
          {...aria}
        >
          <SelectPrimitive.Value placeholder={placeholder}>{selected?.label}</SelectPrimitive.Value>
          <SelectPrimitive.Icon>
            <ChevronDown className="w-4 h-4 text-zinc-400 dark:text-zinc-500" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={6}
            className="z-[1100] overflow-hidden rounded-2xl border border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-zinc-900 shadow-soft dark:shadow-glass-dark min-w-[var(--radix-select-trigger-width)]"
          >
            <SelectPrimitive.Viewport className="p-1.5">
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value === '' ? EMPTY_VALUE_SENTINEL : option.value}
                  disabled={option.disabled ?? false}
                  className={cn(
                    'relative flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer select-none outline-none transition-colors duration-150',
                    'hover:bg-brand-50 dark:hover:bg-white/5 data-[highlighted]:bg-brand-50 dark:data-[highlighted]:bg-white/5',
                    'data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed',
                  )}
                >
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator>
                    <Check className="w-4 h-4 text-brand-500" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </FieldChrome>
  );
}
